/**
 * Human-readable labels for MCP tool names.
 * Used in the spectator activity feed to make tool calls understandable to non-technical viewers.
 */

export const TOOL_LABELS: Record<string, { label: string; icon: string }> = {
  // Search
  search_activities: { label: 'Searching processes', icon: '🔍' },
  search_biosphere: { label: 'Searching emissions', icon: '🌿' },
  search_methods: { label: 'Searching impact methods', icon: '📊' },
  read_activity: { label: 'Reading process details', icon: '📖' },

  // Calculate
  calculate_standard: { label: 'Running impact assessment', icon: '⚡' },
  calculate_custom: { label: 'Calculating custom model', icon: '⚡' },
  calculate_framework: { label: 'Running framework assessment', icon: '⚡' },
  get_task_status: { label: 'Fetching results', icon: '📥' },

  // Reference
  validate_method: { label: 'Validating method', icon: '✅' },
  validate_unit: { label: 'Validating unit', icon: '✅' },
  get_units: { label: 'Looking up units', icon: '📏' },
  list_method_categories: { label: 'Browsing impact categories', icon: '📋' },
  get_biosphere_compartments: { label: 'Browsing compartments', icon: '🌍' },
  get_calculation_history: { label: 'Loading calculation history', icon: '📜' },
  get_calculation_stats: { label: 'Loading usage statistics', icon: '📈' },

  // Power search
  advanced_search: { label: 'Running advanced query', icon: '🔬' },
  get_graph_schema: { label: 'Exploring database structure', icon: '🗂️' },

  // Custom modeling — Projects
  create_project: { label: 'Creating project', icon: '📁' },
  list_projects: { label: 'Listing projects', icon: '📁' },
  get_project: { label: 'Loading project', icon: '📁' },
  update_project: { label: 'Updating project', icon: '✏️' },
  delete_project: { label: 'Deleting project', icon: '🗑️' },
  export_project: { label: 'Exporting project', icon: '📤' },

  // Custom modeling — Activities
  create_activity: { label: 'Defining new process', icon: '🏭' },
  list_activities: { label: 'Listing processes', icon: '🏭' },
  get_activity: { label: 'Loading process', icon: '🏭' },
  update_activity: { label: 'Updating process', icon: '✏️' },
  delete_activity: { label: 'Removing process', icon: '🗑️' },

  // Custom modeling — Exchanges
  create_database_exchange: { label: 'Linked to ecoinvent', icon: '🔗' },
  create_custom_exchange: { label: 'Linked custom process', icon: '🔗' },
  list_exchanges: { label: 'Viewing inputs & outputs', icon: '↔️' },
  update_exchange: { label: 'Updating exchange', icon: '✏️' },
  delete_exchange: { label: 'Removing exchange', icon: '🗑️' },

  // Hotspot analysis
  calculate_hotspots: { label: 'Analyzing impact hotspots', icon: '🔥' },
  calculate_hotspots_custom: { label: 'Analyzing custom hotspots', icon: '🔥' },

  // Scenario analysis
  create_scenario: { label: 'Saving scenario', icon: '🔀' },
  list_scenarios: { label: 'Listing scenarios', icon: '🔀' },
  delete_scenario: { label: 'Deleting scenario', icon: '🗑️' },
  rebase_scenario: { label: 'Rebasing scenario', icon: '🔀' },
  calculate_scenarios: { label: 'Running scenario analysis', icon: '⚡' },

  // LCI export
  get_lci_results: { label: 'Extracting flow inventory', icon: '📊' },
}

/**
 * Get human-readable label for a tool name.
 * Falls back to formatting the raw name if not in the map.
 */
export function getToolLabel(toolName: string): { label: string; icon: string } {
  if (TOOL_LABELS[toolName]) return TOOL_LABELS[toolName]
  // Fallback: convert snake_case to Title Case
  const label = toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return { label, icon: '🔧' }
}
